import React, { forwardRef, useMemo } from "react";
import { Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import EmptyList from "go-core/components/EmptyList";
import { parseToSelectedDateFilter } from "../../services/charts/chartService";
import useDashboardContext from "../../services/hooks/useDashboardContext";
import { useDashboardDraggable } from "../../services/hooks/useDashboardDraggable";
import { useDashboardReportAggregate } from "../../services/hooks/useDashboardReportAggregate";
import {
	DashboardCardConfig,
	DashboardGridItem,
	DashboardReportType,
	DraggableComponentProps,
} from "../../services/types";
import ReportItemWrapper from "./ReportItemWrapper/ReportItemWrapper";
import ReportRenderer from "./ReportRenderer";

const DraggableDashboardReportCategory = forwardRef<HTMLDivElement, DraggableComponentProps>(
	({ style, className, onMouseDown, onMouseUp, onTouchEnd, setNonMobileLayout }, ref): JSX.Element => {
		const { t } = useTranslation();
		const { state, isEditMode } = useDashboardContext();
		const chartDataRangeType = state?.chartsConfig?.reportCategory.range;
		const chartType = state?.chartsConfig.reportCategory.type;
		const { reportAggregateType } = useDashboardReportAggregate(DashboardReportType.CATEGORY);
		const refChildren = ref && "current" in ref ? ref.current?.children?.[0]?.children : undefined;
		const { gridItemZIndex, cursorType, onMouseEnter, onMouseLeave, onCustomMouseDown, onCustomMouseUp } =
			useDashboardDraggable(DashboardGridItem.CATEGORY, setNonMobileLayout, refChildren, onMouseDown, onMouseUp);

		const report = useMemo(
			() => ({
				sub_report: state?.data?.category_reports?.reports,
			}),
			[state?.data?.category_reports?.reports]
		);
		const selectedFilters = useMemo(
			() => parseToSelectedDateFilter(state.filtersConfig.selectedFilter.dateRange),
			[state.filtersConfig.selectedFilter.dateRange]
		);
		const renderView = useMemo(() => {
			if (!report.sub_report || !report.sub_report.length) {
				return <EmptyList />;
			}
			return (
				<ReportRenderer
					data={report.sub_report}
					selectedFilters={selectedFilters}
					chartDataRangeType={chartDataRangeType}
					type={chartType}
					reportType={DashboardReportType.CATEGORY}
				/>
			);
		}, [chartType, reportAggregateType, chartDataRangeType, report]);

		const config = {
			title: t("modules.dashboard.field.top_categories.title"),
			reportNameToRedirectTo: "categories",
			type: "CATEGORY_REPORT",
			chartDataRangeType,
			chartType,
			chartConfigName: "reportCategory",
			cursorType,
		} as DashboardCardConfig;

		return (
			<Card
				className={`${className} ${isEditMode ? "dashboard-edit-mode-element-active" : ""} `}
				style={{ ...style, zIndex: gridItemZIndex, cursor: cursorType }}
				onMouseDown={onCustomMouseDown}
				onMouseUp={onCustomMouseUp}
				onTouchEnd={onTouchEnd}
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
				ref={ref}
			>
				<Card.Body className="position-relative">
					<ReportItemWrapper config={config}>{renderView}</ReportItemWrapper>
				</Card.Body>
			</Card>
		);
	}
);

DraggableDashboardReportCategory.displayName = "DraggableDashboardReportCategory";

export default DraggableDashboardReportCategory;
